import { jwtDecode } from "jwt-decode";
import { showToast } from "../context/ToastHandle";
import { requestHardReload } from "../services/hardReload";

const appVersion = '1.6.2'; // Update as needed or fetch from config

/** Safely decode the JWT from localStorage. Returns null on any issue. */
export function getDecodedToken() {
  const token = localStorage.getItem('token');
  if (!token || typeof token !== 'string' || token.split('.').length !== 3) return null;

  try {
    return jwtDecode(token);
  } catch (err) {
    console.error('JWT decode failed:', err);
    return null;
  }
}

/** Optionally refresh the token from server if online. */
export async function syncTokenWithServer() {
  if (!navigator.onLine) return;

  const token = localStorage.getItem('token');
  if (!token) return;

  // const appVersion = '1.5.1'; // Update as needed or fetch from config
  localStorage.setItem('version', appVersion);

  try {
    const res = await fetch('https://jmew.xyz/aac/api/users.php', {
      method: 'GET',
      headers: { 
        Authorization: `Bearer ${token}`,
        'X-App-Version': appVersion
      }
    });

    if (res.status === 401 || res.status === 409) {
      // Token is no longer valid — force logout
      forceLogout(res.status === 409 ? 'multipleusers' : 'expired');
      return;
    }

    const data = await res.json().catch(() => ({}));
    const { token: newToken, update_required, latest_version } = data || {};

    if (res.ok && newToken) {
      localStorage.setItem('token', newToken);
    }

    if (update_required && latest_version) {
      // Optional: show toast or modal prompt
      console.log(`Update required: latest version is ${latest_version}`);
      showToast(`A new version (${latest_version}) is available. Please update your app.`, 'info', false);
      requestHardReload();
    }
  } catch (err) {
    console.error('Token sync failed:', err);
  }
}

/// Board usage and limits (new functions, non-breaking)

export async function getBoardUsage() {
  if (!navigator.onLine) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  // const appVersion = '1.5.1';
  try {
    const res = await fetch('https://jmew.xyz/aac/api/countBoards.php', {
      method: 'GET',
      headers: { 
        Authorization: `Bearer ${token}`,
        'X-App-Version': appVersion
      }
    });
    const data = await res.json();
    const count = Number(data?.count ?? 0);
    const plan = getAccountType();

    const boardsByPlan = {
      free: 5,
      basic: 5,
      premium: 40,
      unlimited: 1000,
    };

    const limit = boardsByPlan[plan] ?? 0;

    return {
      plan,
      limit,
      count,
      remaining: Math.max(0, limit - count),
      isLimitReached: count >= limit,
    };
  } catch (err) {
    console.error('Failed to fetch board count:', err);

    return {
      plan,
      limit,
      count: 0,
      remaining: limit,
      isLimitReached: false,
      error: true,
    };
  }
}

/** Plan/Account type with org override when present (backwards compatible). */
export function getAccountType() {
  const decoded = getDecodedToken();
  if (!decoded) return 'free';
  // If in an organisation, backend sets acc_type to the org's plan
  return decoded.acc_type || 'free';
}

export function getTrialExpiry() {
  const decoded = getDecodedToken();
  return decoded?.trial_expiry || null;
}

export function getAccTypeTime() {
  const decoded = getDecodedToken();
  return decoded?.acc_type_time || null;
}

/**
 * Days remaining until expiry (or a friendly string).
 * Behaviour preserved, just safer date handling.
 */
export function getDaysRemaining(mode = 'number') {
  const plan = getAccountType();
  const expiry = getTrialExpiry();
  const accTypeTime = getAccTypeTime();
  const targetDate = plan === 'free' ? expiry : accTypeTime;

  if (!targetDate) {
    return mode === 'string' ? 'No time remaining' : 0;
  }

  const now = new Date();
  const future = new Date(targetDate);
  const ms = future.getTime() - now.getTime();

  if (!isFinite(ms) || ms <= 0) {
    return mode === 'string' ? 'Expired' : 0;
  }

  const totalMinutes = Math.floor(ms / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (mode === 'string') {
    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    return parts.join(' ') || 'Less than a minute';
  }

  return days;
}

export function forceUserProfile() {
  const onUpgradePage = window.location.pathname === '/aac/profile' && new URLSearchParams(window.location.search).get('upgrade') === '1';
  if (onUpgradePage) return;
  //window.location = '/aac/profile?upgrade=1';
  console.log( onUpgradePage, 'Redirecting to profile for upgrade', window.location.pathname);
}

/** True if access should be considered expired (preserves old logic, adds org inactive check). */
export function isAccessExpired() {
  const decoded = getDecodedToken();

  // If part of an org and org is inactive, treat as expired immediately.
  if (decoded?.org_id && decoded?.org_status && !['active', 'trialing'].includes(decoded.org_status)) {
    return true;
  }

  const plan = getAccountType();
  const expiry = getTrialExpiry();
  const accTypeTime = getAccTypeTime();
  const targetDate = plan === 'free' ? expiry : accTypeTime;

  if (!targetDate) return true;
  return new Date(targetDate) < new Date();
}

/** Feature flags by plan (unchanged). */
export function hasFeature(feature) {
  const plan = getAccountType();

  const featuresByPlan = {
    free: [],
    basic: ['upload', 'select_voice'],
    premium: ['upload', 'offline', 'extra_boards', 'select_voice'],
    unlimited: ['upload', 'offline', 'extra_boards', 'folders', 'custom_symbols', 'pdf_export', 'symbol_request', 'select_voice'],
  };

  return featuresByPlan[plan]?.includes(feature) || false;
}

/** Board limits by plan (unchanged). */
export function getBoardLimit() {
  const plan = getAccountType();

  const boardsByPlan = {
    free: 5,
    basic: 5,
    premium: 40,
    unlimited: 1000,
  };

  return boardsByPlan[plan] ?? 0;
}

/** True if token is actually expired (unchanged logic). */
export const isTokenExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) return false; // Let routes handle unauthenticated state

  try {
    const decoded = jwtDecode(token);
    const now = Date.now() / 1000;
    if (!decoded.exp) return false;
    return decoded.exp < now;
  } catch (err) {
    console.error('❌ Failed to decode token:', err);
    return true;
  }
};

let logoutTriggered = false;

export const forceLogout = (reason = 'expired') => {

  console.log('⚠️ forceLogout called with reason:', reason, new Error().stack);

  if (logoutTriggered) return; // prevent duplicate redirects
  logoutTriggered = true;

  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // optionally use payload info
      // eslint-disable-next-line no-empty
    } catch (err) {
      console.error('❌ Failed to decode token during logout');
    }
  }

  // Clear auth-related storage
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('name');
  localStorage.removeItem('pin');
  localStorage.removeItem('decodedToken');
  localStorage.removeItem('aac-voice');

  // Redirect to login with reason
  const query = `?${reason}=1`;
  window.location.href = '/aac/login' + query;
};

/* -----------------------------
   NEW ORG-AWARE HELPERS (non-breaking)
   ----------------------------- */

/** Returns true if the current user is the org owner. */
export function isOrgOwner() {
  const d = getDecodedToken();
  return !!(d && d.org_id && (d.org_owner || d.org_role === 'owner'));
}

/** Returns the current organisation ID or null. */
export function getOrgId() {
  const d = getDecodedToken();
  return d?.org_id ?? null;
}

/** Returns the seat limit from the token (set by backend) or null. */
export function getSeatLimit() {
  const d = getDecodedToken();
  return d?.seat_limit ?? null;
}

/** True if the org is inactive (status not active/trialing). */
export function isOrgInactive() {
  const d = getDecodedToken();
  return !!(d?.org_id && d?.org_status && !['active', 'trialing'].includes(d.org_status));
}
